import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/store';
import { CheckCircle2, Loader2 } from 'lucide-react';

export const InviteAcceptPage = () => {
  const { token } = useParams();
  const api = useAuth((s) => s.api);
  const user = useAuth((s) => s.user);
  const navigate = useNavigate();
  const [invite, setInvite] = useState<{ projectId: string; projectName: string; role: 'editor' | 'viewer' } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token || !api) return;
    void api
      .resolveInvite(token)
      .then(({ invite }) => setInvite(invite))
      .catch((err) => setError((err as Error).message));
  }, [token, api]);

  const accept = async () => {
    if (!token || !api) return;
    setBusy(true);
    setError(null);
    try {
      await api.acceptInvite(token);
      navigate(`/projects/${invite?.projectId}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="flex min-h-full items-center justify-center bg-slate-950 p-6 text-slate-100">
        <div className="card max-w-md text-center">
          <h2 className="mb-2 text-lg font-semibold">Sign in to accept this invite</h2>
          <p className="mb-4 text-sm text-slate-400">
            You'll be added as a {invite ? invite.role : '…'} to the project.
          </p>
          <Link
            className="btn-primary"
            to={`/login?next=${encodeURIComponent(`/invites/${token}`)}`}
          >
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-slate-950 p-6 text-slate-100">
      <div className="card max-w-md">
        <h2 className="mb-2 text-lg font-semibold">Project invite</h2>
        {error && <p className="mb-3 text-sm text-red-400">{error}</p>}
        {invite ? (
          <>
            <p className="mb-1 text-sm text-slate-300">
              You've been invited to join{' '}
              <span className="font-medium text-archer-300">{invite.projectName}</span> as a{' '}
              <span className="font-medium">{invite.role}</span>.
            </p>
            <button className="btn-primary mt-4 w-full" onClick={() => void accept()} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Accept invite
            </button>
          </>
        ) : (
          <p className="text-sm text-slate-400">Looking up invite…</p>
        )}
      </div>
    </div>
  );
};
